/**
 * Models for Notaire API responses and data structures
 */

import { StatusDto, NotaryDto, SuccessionFileDto } from "../dto/api-dtos";

export interface NotaireApiResponse {
  id: number;
  notaryOfficeExternalId: number;
  nomEtude: string | null;
  number: string | null;
  lastName: string | null;
  firstName: string | null;
  department: string | null;
  createdAt: string;
  status: StatusDto;
  nombreDossier: number;
  notaries: NotaryDto[] | null;
  successionFiles: SuccessionFileDto[] | null;
}

export interface NotaireDetail {
  id: number;
  lastName: string;
  firstName: string;
  nationality: string;
  birthDate: string;
  country: string;
  city: string;
  socialSecurityNumber: string;
  maritalStatus: number;
  email: string;
  phone: string;
  createdAt: string;
  modifiedAt: string;
  createdBy: number;
  modifiedBy: number;
  gender: number;
  landlinePhone: string;
  workPhone: string;
  profilePhoto: string;
  profession: string;
  codeCsn: string;
  inscriptionDate: string;
  registrationDate: string;
  actif: boolean;
}

export interface SuccessionFile {
  id: number;
  successionFileId: number;
  openedAt: string;
  closedAt: string;
  digitalDeathRequestAt: string;
  digitalDeathClosedAt: string;
  status: number;
  phase: number;
  createdAt: string;
  createdBy: number;
  modifiedAt: string;
  modifiedBy: number;
  notaryOfficeId: number;
  successorId: number;
  deceasedId: number;
  deceasedFirstName: string;
  deceasedLastName: string;
  notaries: NotaireDetail[];
}

export interface NotaireQueryParams {
  paginated?: boolean;
  includeNotaries?: boolean;
  includeSuccessions?: boolean;
  page?: number;
  pageSize?: number;
  department?: string;
  status?: number;
  search?: string;
}

export interface ApiPaginatedResponse<T> {
  pagination: {
    pageNumber: number;
    pageSize: number;
    count: number;
    countPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  data: T[];
}

/**
 * Transformed data structure for the UI table
 */
export interface NotaireTableData {
  id: number;
  etude: string;
  nomPrenom: string;
  departement: string;
  dateInscription: Date;
  nombreDossiers: number;
  status: string | null;
}

/**
 * State interface for NotaryOffices store
 */
export interface NotaryOfficesState {
  notaires: NotaireTableData[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  filters: {
    department?: string;
    status?: number;
    search?: string;
  };
  sort: {
    column?: string;
    direction?: 'asc' | 'desc';
  };
}

/**
 * Mapper function to transform API response to table data
 */
export function mapNotaireApiToTableData(apiData: NotaireApiResponse[]): NotaireTableData[] {
  return apiData.map(item => ({
    id: item.id,
    etude: item.nomEtude || item.number || '',
    nomPrenom: `${item.lastName || ''} ${item.firstName || ''}`.trim() || '-',
    departement: item.department || '-',
    dateInscription: new Date(item.createdAt|| new Date()),
    nombreDossiers: item.nombreDossier || 0,
    status: item.status?.name || null
  }));
}
